import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BookOpenIcon, 
  UserGroupIcon, 
  ClockIcon,
  ArrowTrendingUpIcon,
  EyeIcon,
  DocumentCheckIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  ArrowRightIcon,
  ChevronRightIcon,
  CalendarDaysIcon,
  TrophyIcon,
  UsersIcon,
  PresentationChartLineIcon,
  ClipboardDocumentListIcon,
  StarIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  WrenchScrewdriverIcon,
  GlobeAltIcon,
  KeyIcon,
  DocumentTextIcon,
  UserPlusIcon,
  BookmarkIcon,
  ShareIcon,
  PrinterIcon,
  MagnifyingGlassIcon,
  ChartPieIcon,
  ArrowDownTrayIcon,
  PencilIcon,
  TrashIcon,
  EyeSlashIcon,
  CheckBadgeIcon,
  XCircleIcon,
  CalendarIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  IdentificationIcon,
  GraduationCapIcon,
  ClipboardDocumentCheckIcon,
  DocumentDuplicateIcon,
  QrCodeIcon,
  LinkIcon,
  CloudArrowUpIcon,
  CloudArrowDownIcon,
  ServerIcon,
  CpuChipIcon,
  WifiIcon,
  SignalIcon,
  Battery100Icon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  LightBulbIcon,
  SparklesIcon,
  FireIcon,
  HeartIcon,
  HandThumbUpIcon,
  ChatBubbleLeftRightIcon,
  BellIcon,
  Cog6ToothIcon,
  UserCircleIcon,
  ArrowPathIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  ForwardIcon,
  BackwardIcon,
  VolumeUpIcon,
  VolumeXIcon,
  AdjustmentsHorizontalIcon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  TabletIcon,
  TvIcon,
  SpeakerWaveIcon,
  MicrophoneIcon,
  CameraIcon,
  PhotoIcon,
  VideoCameraIcon,
  FilmIcon,
  MusicalNoteIcon,
  SpeakerXMarkIcon,
  SignalSlashIcon,
  Battery50Icon,
  Battery25Icon,
  Battery0Icon,
  PowerIcon,
  BoltIcon,
  HomeIcon,
  OfficeBuildingIcon,
  AcademicCapIcon as SchoolIcon,
  LibraryIcon,
  ArchiveBoxIcon,
  ArchiveBoxXMarkIcon,
  InboxIcon,
  InboxArrowDownIcon,
  InboxStackIcon,
  FolderIcon,
  FolderOpenIcon,
  FolderPlusIcon,
  FolderMinusIcon,
  DocumentIcon,
  DocumentPlusIcon,
  DocumentMinusIcon,
  DocumentArrowUpIcon,
  DocumentArrowDownIcon,
  DocumentXMarkIcon,
  DocumentMagnifyingGlassIcon,
  DocumentChartBarIcon,
  DocumentReportIcon,
  DocumentSpreadsheetIcon,
  DocumentPresentationChartLineIcon,
  DocumentCodeBracketIcon,
  DocumentCodeBracketSquareIcon,
  CodeBracketIcon,
  CodeBracketSquareIcon,
  CommandLineIcon,
  TerminalIcon,
  CursorArrowRaysIcon,
  CursorArrowRippleIcon,
  HandRaisedIcon,
  HandThumbDownIcon,
  FingerPrintIcon,
  FaceSmileIcon,
  FaceFrownIcon,
  FaceMehIcon,
  EmojiHappyIcon,
  EmojiSadIcon,
  EmojiNeutralIcon,
  HeartHandshakeIcon,
  HandshakeIcon,
  GiftIcon,
  GiftTopIcon,
  StarHalfIcon,
  CloudIcon,
  CloudRainIcon,
  CloudSnowIcon,
  CloudLightningIcon,
  CloudSunIcon,
  CloudMoonIcon,
  CloudCheckIcon,
  CloudXMarkIcon,
  CloudExclamationIcon,
  CloudQuestionMarkIcon,
  CloudSlashIcon,
  ServerStackIcon,
  CircleStackIcon,
  MemoryIcon,
  HardDriveIcon
} from '@heroicons/react/24/outline';
import { 
  LineChart, 
  Line, 
  AreaChart, 
  Area, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';
import { Card } from '../../components/ui/card';
import dashboardService from '../../services/dashboardService';

const Dashboard = () => {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState('1month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for real data
  const [stats, setStats] = useState({
    totalBooks: 0,
    activeSchools: 0,
    totalStudents: 0,
    activationCodesUsed: 0
  });
  const [trends, setTrends] = useState({
    books: 0,
    schools: 0,
    students: 0,
    activationCodes: 0
  });
  const [activationCodeStats, setActivationCodeStats] = useState([]);
  const [userAssetStats, setUserAssetStats] = useState([]);

  const quickActions = [
    { name: 'Thêm sách mới', icon: BookOpenIcon, color: 'bg-blue-500', href: '/books/new' },
    { name: 'Quản lý trường học', icon: SchoolIcon, color: 'bg-green-500', href: '/admin/school' },
    { name: 'Mã kích hoạt', icon: KeyIcon, color: 'bg-orange-500', href: '/books/activation-codes' }
  ];

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch all data in parallel
      const [statsData, activationCodeData, userAssetData] = await Promise.all([
        dashboardService.getBookDashboardStats(timeRange),
        dashboardService.getActivationCodeStats(timeRange),
        dashboardService.getUserAssetStats(timeRange)
      ]);

      setStats({
        totalBooks: statsData.totalBooks,
        activeSchools: statsData.activeSchools,
        totalStudents: statsData.totalStudents,
        activationCodesUsed: statsData.activationCodesUsed
      });
      
      if (statsData.trends) {
        setTrends(statsData.trends);
      }

      setActivationCodeStats(activationCodeData);
      setUserAssetStats(userAssetData);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Không thể tải dữ liệu dashboard. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [timeRange]);

  const StatCard = ({ title, value, icon: Icon, trend, color = "text-blue-500" }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card className="p-6 hover:shadow-lg transition-shadow duration-300">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
            {trend && (
              <div className="flex items-center mt-2">
                <ArrowTrendingUpIcon className="w-4 h-4 text-green-500 mr-1" />
                <span className="text-sm text-green-500 font-medium">{trend}%</span>
                <span className="text-sm text-gray-500 ml-1">so với tháng trước</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-full bg-opacity-10 ${color.replace('text-', 'bg-')}`}>
            <Icon className={`w-6 h-6 ${color}`} />
          </div>
        </div>
      </Card>
    </motion.div>
  );

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-64 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="h-80 bg-gray-200 rounded-lg"></div>
            <div className="h-80 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center">
          <ExclamationCircleIcon className="w-5 h-5 mr-2" />
          <span>{error}</span>
          <button 
            onClick={fetchDashboardData} 
            className="ml-auto px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Thử lại
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Dashboard Quản Lý Sách</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Hệ thống quản lý và phân phối sách cho các trường học, lớp học và học sinh.</p>
        </div>
        <div className="flex items-center space-x-2 mt-4 sm:mt-0">
          <select 
            value={timeRange} 
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
          >
            <option value="1month">1 tháng</option>
            <option value="3months">3 tháng</option>
            <option value="6months">6 tháng</option>
            <option value="12months">12 tháng</option>
          </select>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <StatCard 
          title="Tổng số sách" 
          value={stats.totalBooks.toLocaleString()} 
          icon={BookOpenIcon} 
          trend={trends.books || 0}
          color="text-blue-500"
        />
        <StatCard 
          title="Mã kích hoạt đã dùng" 
          value={`${stats.activationCodesUsed}%`} 
          icon={KeyIcon} 
          trend={trends.activationCodes || 0}
          color="text-orange-500"
        />
      </div>

      {/* Charts Section */}
      {/* Activation Code Statistics Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Thống kê mã kích hoạt</h3>
            <KeyIcon className="w-5 h-5 text-gray-400" />
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={activationCodeStats}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value, name) => {
                const labels = {
                  codesActivated: 'Mã đã kích hoạt',
                  codesCreated: 'Mã được tạo'
                };
                return [value, labels[name] || name];
              }} />
              <Bar dataKey="codesActivated" fill="#10B981" name="Mã đã kích hoạt" />
              <Bar dataKey="codesCreated" fill="#3B82F6" name="Mã được tạo" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </motion.div>

      {/* User Asset Statistics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
      >
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Thống kê phân phối và tải xuống sách</h3>
            <ArrowDownTrayIcon className="w-5 h-5 text-gray-400" />
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={userAssetStats}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value, name) => {
                const labels = {
                  assetsCreated: 'Sách được phân phối',
                  assetsDownloaded: 'Lượt tải xuống',
                  uniqueDownloaders: 'Người dùng tải xuống'
                };
                return [value, labels[name] || name];
              }} />
              <Line type="monotone" dataKey="assetsCreated" stroke="#3B82F6" strokeWidth={2} name="Sách được phân phối" />
              <Line type="monotone" dataKey="assetsDownloaded" stroke="#10B981" strokeWidth={2} name="Lượt tải xuống" />
              <Line type="monotone" dataKey="uniqueDownloaders" stroke="#F59E0B" strokeWidth={2} name="Người dùng tải xuống" />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Thao tác nhanh</h3>
            <PlusIcon className="w-5 h-5 text-gray-400" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {quickActions.map((action, index) => (
              <motion.button
                key={action.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="flex flex-col items-center space-y-3 p-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-center"
                onClick={() => navigate(action.href)}
              >
                <div className={`p-3 rounded-lg ${action.color} text-white`}>
                  <action.icon className="w-6 h-6" />
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-white">{action.name}</span>
              </motion.button>
            ))}
          </div>
        </Card>
      </motion.div>

      {/* System Modules */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Hệ thống quản lý sách và phân phối</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all"
            >
              <BookOpenIcon className="w-8 h-8 text-blue-500 mb-3" />
              <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Quản lý sách</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm">
                Quản lý thư viện sách và nội dung
              </p>
              <button 
                onClick={() => navigate('/books/list')}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Truy cập sách
              </button>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all"
            >
              <SchoolIcon className="w-8 h-8 text-green-500 mb-3" />
              <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Trường học</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm">
                Quản lý trường học và lớp học
              </p>
              <button 
                onClick={() => navigate('/admin/school')}
                className="w-full bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Quản lý trường
              </button>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all"
            >
              <KeyIcon className="w-8 h-8 text-purple-500 mb-3" />
              <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Mã kích hoạt</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm">
                Quản lý mã kích hoạt và phân quyền
              </p>
              <button 
                onClick={() => navigate('/books/activation-codes')}
                className="w-full bg-purple-500 hover:bg-purple-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Quản lý mã
              </button>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02 }}
              className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-all"
            >
              <UserGroupIcon className="w-8 h-8 text-orange-500 mb-3" />
              <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Học sinh</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm">
                Quản lý học sinh và tiến độ học tập
              </p>
              <button 
                onClick={() => navigate('/admin/school-students')}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Quản lý học sinh
              </button>
            </motion.div>
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

export default Dashboard; 